/**
 * Navigation utilities to preserve Onshape URL parameters during internal app navigation.
 * 
 * These utilities ensure that query parameters (documentId, workspaceId, elementId, etc.)
 * are preserved when navigating between routes within the app, while allowing Onshape
 * to update parameters when the context changes (e.g., switching to a different part studio).
 * 
 * Parameters will NOT be added back if they've been manually removed from the URL.
 */

/**
 * Preserves current query parameters when constructing a new path.
 * Only preserves parameters if they exist in the current URL (respects manual removal).
 * 
 * @param {string} path - The target path (e.g., '/chats/123' or '/')
 * @returns {string} - Path with current query parameters appended (if they exist)
 * 
 * @example
 * // If current URL is: https://app.fastenai.dev/chats/123?documentId=abc&workspaceId=def
 * preserveQueryParams('/chats/456')
 * // Returns: '/chats/456?documentId=abc&workspaceId=def'
 * 
 * @example
 * // If current URL has no query params: https://app.fastenai.dev/chats/123
 * preserveQueryParams('/chats/456')
 * // Returns: '/chats/456' (no params added)
 */
export function preserveQueryParams(path) {
  const currentSearch = window.location.search;
  
  // If there are no query params in current URL, don't add any
  // This respects when user manually removes params
  if (!currentSearch || currentSearch.length === 0) {
    return path;
  }
  
  // Preserve existing query params
  // Remove leading '?' if path already has query params (shouldn't happen, but be safe)
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${currentSearch.substring(1)}`; // substring(1) removes leading '?'
}

/**
 * Wrapper for React Router's navigate function that preserves query parameters.
 * 
 * @param {Function} navigate - React Router's navigate function from useNavigate()
 * @param {string} path - The target path
 * @param {Object} options - Navigation options (e.g., { replace: true })
 * @returns {void}
 * 
 * @example
 * const navigate = useNavigate();
 * navigateWithParams(navigate, '/chats/123', { replace: true });
 */
export function navigateWithParams(navigate, path, options = {}) {
  const pathWithParams = preserveQueryParams(path);
  navigate(pathWithParams, options);
}

/**
 * Helper to get a path with preserved query parameters.
 * Alias for preserveQueryParams for clarity in certain contexts.
 * 
 * @param {string} path - The target path
 * @returns {string} - Path with current query parameters appended (if they exist)
 */
export function getPreservedPath(path) {
  return preserveQueryParams(path);
}

